import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { formatApiErrorDetail, validatePassword } from "@/lib/authForms";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const pwIssues = validatePassword(password);
  const mismatch = password && confirm && password !== confirm;
  const canSubmit = !!token && pwIssues.length === 0 && !mismatch && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      await refresh();
      setDone(true);
      setTimeout(() => navigate("/app", { replace: true }), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, "Could not reset password."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell
        title="Reset link missing"
        subtitle="This page expects a reset token from your email."
        footer={<Link to="/forgot-password" className="text-[#D4AF37] hover:text-[#F3CE5A]">Request a new link →</Link>}
      >
        <div className="mt-8 rounded-2xl border border-white/10 p-6 text-center" data-testid="reset-missing">
          <p className="text-sm text-white/65">Open the link from your inbox to land here with a valid token.</p>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="Signing you in…">
        <div className="mt-8 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-6 text-center" data-testid="reset-success">
          <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
          <p className="font-display text-xl text-white">All set.</p>
          <p className="text-sm text-white/65 mt-2">Taking you to the studio…</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set a new password"
      subtitle="Choose a strong password — at least 8 characters with a letter and a number."
      footer={<Link to="/signin" className="text-[#D4AF37] hover:text-[#F3CE5A]">← Back to sign in</Link>}
    >
      <form onSubmit={submit} className="mt-7 space-y-4" data-testid="reset-form">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">New password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              data-testid="reset-password-input"
              className="w-full bg-[#141414] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              placeholder="Min 8 chars · letters + numbers"
            />
          </div>
          {password && pwIssues.length > 0 && (
            <p className="text-[10px] text-amber-300/80 mt-1.5">Password needs: {pwIssues.join(", ")}.</p>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Confirm password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="reset-confirm-input"
              className="w-full bg-[#141414] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              placeholder="Re-type your new password"
            />
          </div>
          {mismatch && <p className="text-[10px] text-amber-300/80 mt-1.5">Passwords don't match.</p>}
        </div>
        {error && (
          <p data-testid="reset-error" className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="reset-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</> : <>Update password</>}
        </button>
      </form>
    </AuthShell>
  );
}
